import {
  ArrowRightIcon,
  DocumentIcon,
  EyeIcon,
  EyeOffIcon,
  GiftIcon,
  SpeakerphoneIcon,
  StatusOnlineIcon
} from "@heroicons/react/solid";
import animalHash from "angry-purple-tiger";
import { filter, flatten, isEmpty, orderBy } from "lodash";
import moment from "moment";
import { stringifyUrl } from "query-string";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import LoadingIcon from "../../../../components/icons/LoadingIcon";
import classNames from "../../../../utils/classNames";
import { useHntMarket } from "../../../../utils/useHntMarket";
import useMapConfig from "../../../../utils/useMapConfig";
import usePrevious from "../../../../utils/usePrevious";
import ProofOfCoverageActivitySidebar from "./ProofOfCoverageActivitySidebar";

interface Props {
  hotspotId: string;
}

const HotspotActivityTab: React.FC<Props> = ({ hotspotId }) => {
  const [filteredActivityType, setFilteredActivityType] = useState<
    ActivityType | "all"
  >("all");
  const { displayInCurrency } = useHntMarket();
  const renderDistance = useMapConfig((state) => state.renderDistance);
  const [activityData, setActivityData] = useState<Activity[]>([]);
  const [fetchStatus, setFetchStatus] = useState<
    "idle" | "fetching" | "fetched"
  >("idle");
  const [fetchedPage, setFetchedPage] = useState(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentPoCActivity, setCurrentPoCActivity] =
    useState<PoCActivity | null>(null);

  const fetchHotspotActivities = useCallback(
    async (hotspotId: string, page: number = 0): Promise<void> => {
      setFetchStatus("fetching");
      setFetchedPage(page);

      const response = await fetch(
        stringifyUrl({
          url: `${process.env.REACT_APP_ETL_API}/hotspot/activities/${hotspotId}/`,
          query: { page }
        })
      );

      const apiResponse: HotspotActivitiesResponse = await response.json();

      if (apiResponse?.data) {
        const { witnesses, challengees, challengers, rewards, data_packets } =
          apiResponse.data;

        const activityData = flatten<Activity>([
          witnesses.map((x) => ({ ...x, type: "witnesses" })),
          challengees.map((x) => ({ ...x, type: "challengees" })),
          challengers.map((x) => ({ ...x, type: "challengers" })),
          rewards.map((x) => ({ ...x, type: "rewards" })),
          data_packets.map((x) => ({ ...x, type: "data_packets" }))
        ]);

        setActivityData((prevActivity) =>
          prevActivity.concat(orderBy(activityData, "time", "desc"))
        );

        setFetchStatus("fetched");
      }
    },
    []
  );

  useEffect(() => {
    if (fetchStatus === "idle") {
      fetchHotspotActivities(hotspotId);
    }
  }, [fetchStatus, fetchHotspotActivities, hotspotId]);

  const prevHotspotId = usePrevious(hotspotId);
  useEffect(() => {
    if (prevHotspotId !== hotspotId) {
      setFetchStatus("idle");
      setActivityData([]);
      setFetchedPage(0);
      setCurrentPoCActivity(null);
      setIsSidebarOpen(false);
    }
  }, [hotspotId, prevHotspotId]);

  const filteredActivies = useMemo(
    () =>
      filteredActivityType === "all"
        ? activityData
        : filter(activityData, { type: filteredActivityType }),
    [activityData, filteredActivityType]
  );

  const renderSubtitle = useCallback(
    (activity: Activity) => {
      switch (activity.type) {
        case "rewards":
          return `+${displayInCurrency(
            (activity as HotspotRewardActivity).amount,
            {
              needsDivision: true,
              withHntSuffix: true,
              minimumFractionDigits: 6,
              maximumFractionDigits: 6
            }
          )}`;
        case "witnesses":
          return (
            <>
              <span className="mr-1">by</span>

              <Link
                to={`/hotspots/${activity.beaconer}/status`}
                className="text-gray-900 dark:text-white cursor-pointer hover:text-gray-600 font-semibold dark:hover:text-white hover:underline"
              >
                {animalHash((activity as PoCActivity).beaconer)}
              </Link>

              <span className="mx-1">in</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {activity.beaconer_location}
              </span>
            </>
          );
        case "challengees":
          return (
            <>
              <span className="mr-1">from</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {activity.beaconer_location}
              </span>
            </>
          );
        case "challengers":
          return (
            <>
              <span className="mr-1">challenged</span>

              <Link
                to={`/hotspots/${activity.beaconer}/status`}
                className="text-gray-900 dark:text-white cursor-pointer hover:text-gray-600 font-semibold dark:hover:text-white hover:underline"
              >
                {animalHash(activity.beaconer)}
              </Link>

              <span className="mx-1">in</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {activity.beaconer_location}
              </span>
            </>
          );
        case "data_packets":
          return `${activity.num_dcs} data credit${
            activity.num_dcs === 1 ? "" : "s"
          }, ${activity.num_packets} packet${
            activity.num_packets === 1 ? "" : "s"
          }`;
        default:
          return null;
      }
    },
    [displayInCurrency]
  );

  const renderTitleSuffix = useCallback(
    (activity: Activity) => {
      switch (activity.type) {
        case "witnesses":
          return `from ${renderDistance(
            activity.distance,
            activity.distance > 1000
          )} away`;
        case "challengees":
          return `with ${activity.valid_count} valid and ${activity.invalid_count} invalid witnesses`;
        default:
          return null;
      }
    },
    [renderDistance]
  );

  const renderExtraSubtitle = useCallback((activity: Activity) => {
    switch (activity.type) {
      case "witnesses":
      case "challengers":
      case "challengees":
        return (
          <div
            className="float-right flex items-center hover:text-gray-600 truncate font-semibold dark:hover:text-white hover:underline cursor-pointer"
            onClick={() => {
              setCurrentPoCActivity(activity);
              setIsSidebarOpen(true);
            }}
          >
            Details
            <ArrowRightIcon className="h-3 w-3 inline ml-1" />
          </div>
        );
      default:
        return null;
    }
  }, []);

  const renderActivity = useCallback(
    (activity: Activity, index: number) => (
      <li key={`${activity.hash}-${index}`}>
        <div className="relative pb-8">
          {!isEmpty(filteredActivies) &&
          index !== filteredActivies.length - 1 ? (
            <span
              className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
              aria-hidden="true"
            />
          ) : null}
          <div className="relative flex items-center space-x-3">
            <div className="flex items-center">{renderIcon(activity)}</div>
            <div className="min-w-0 flex-1 flex justify-between space-x-4">
              <div>
                <div>
                  <span className="text-gray-900 dark:text-white mr-1">
                    {renderTitle(activity)}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-300 hidden sm:inline">
                    {renderTitleSuffix(activity)}
                  </span>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-300">
                  {renderSubtitle(activity)}
                </div>
              </div>
              <div className="text-right text-sm whitespace-nowrap text-gray-500 dark:text-gray-300">
                <div>{moment.unix(activity.time).utc().fromNow()}</div>
                <div>{renderExtraSubtitle(activity)}</div>
              </div>
            </div>
          </div>
        </div>
      </li>
    ),
    [filteredActivies, renderExtraSubtitle, renderSubtitle, renderTitleSuffix]
  );

  const renderContent = useMemo(() => {
    const dummyActivities = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];
    if (fetchStatus !== "fetched" && fetchedPage === 0) {
      return (
        <div className="flex flex-col">
          {dummyActivities.map((activity, index) => (
            <div
              key={activity}
              className="flex justify-between mb-9 relative first:mt-1.5"
            >
              <div className="flex flex-1">
                <div className="w-8 h-8 flex-shrink-0 animate-pulse bg-gray-400 rounded-full" />
                <div className="flex flex-col w-full ml-3">
                  <div className="flex w-5/12 mb-2 h-4 animate-pulse bg-gray-400 rounded" />
                  <div className="flex w-7/12 h-4 animate-pulse bg-gray-400 rounded" />
                </div>
              </div>
              <div className="flex w-2/12 h-4 animate-pulse bg-gray-400 rounded" />
              {index !== dummyActivities.length - 1 && (
                <span className="absolute top-4 left-4 -ml-px mt-6 h-7 w-0.5 bg-gray-400" />
              )}
            </div>
          ))}
        </div>
      );
    }

    return (
      <>
        {isEmpty(filteredActivies) ? (
          <div>No activity found so far</div>
        ) : (
          <div className="flow-root">
            <ul className="-mb-8">{filteredActivies.map(renderActivity)}</ul>
          </div>
        )}

        <button
          onClick={() => {
            fetchHotspotActivities(hotspotId, fetchedPage + 1);
          }}
          className="mt-6 w-full sm:w-auto justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 flex items-center disabled:opacity-50 disabled:cursor-default disabled:hover:bg-hotspotty-400"
          disabled={fetchStatus === "fetching"}
        >
          {fetchStatus === "fetching" && (
            <LoadingIcon className="animate-spin -ml-1 mr-3 h-5 w-5" />
          )}
          <span>
            {fetchStatus === "fetching" ? "Loading more" : "Load more"}
          </span>
        </button>
      </>
    );
  }, [
    fetchHotspotActivities,
    fetchStatus,
    fetchedPage,
    filteredActivies,
    hotspotId,
    renderActivity
  ]);

  return (
    <div className="mt-6">
      <div className="mb-6">
        <select
          className="sm:hidden block w-full pl-3 py-2 sm:text-sm rounded-md dark:bg-gray-800"
          onChange={(e) => {
            const type = e.target.value as ActivityType | "all";
            setFilteredActivityType(
              type !== "all" && filteredActivityType === type ? "all" : type
            );
          }}
          value={filteredActivityType}
        >
          {tabs.map((key) => (
            <option key={key} value={key}>
              {key === "all" ? "All" : CONFIG[key]?.name}
            </option>
          ))}
        </select>
        <div className="hidden sm:block">
          <nav className="flex space-x-4" aria-label="Tabs">
            {tabs.map((key) => (
              <div
                key={key}
                className={classNames(
                  "px-3 py-2 font-medium text-sm rounded-md",
                  filteredActivityType === key
                    ? "bg-hotspotty-100 text-hotspotty-700 dark:bg-gray-700 dark:text-white cursor-default"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50 group dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-transparent cursor-pointer"
                )}
                onClick={() => {
                  const type = key as ActivityType | "all";
                  setFilteredActivityType(
                    type !== "all" && filteredActivityType === type
                      ? "all"
                      : type
                  );
                }}
              >
                {key === "all" ? "All" : CONFIG[key]?.name}
              </div>
            ))}
          </nav>
        </div>
      </div>

      {renderContent}

      <ProofOfCoverageActivitySidebar
        activity={currentPoCActivity}
        open={isSidebarOpen}
        onClose={() => {
          setIsSidebarOpen(false);
          setCurrentPoCActivity(null);
        }}
      />
    </div>
  );
};

export default HotspotActivityTab;

//
// Utils
//

type ActivityType =
  | "witnesses"
  | "challengers"
  | "challengees"
  | "rewards"
  | "data_packets";

const tabs = [
  "all",
  "challengees",
  "witnesses",
  "challengers",
  "rewards",
  "data_packets"
];

export interface HotspotWitness {
  hash: string;
  distance: number;
  datarate: string;
  rssi: number;
  snr: number;
  frequency: number;
  valid: boolean;
  timestamp: number;
  channel: number;
  location: string;
  invalid_reason: string | null;
}

interface BaseTxn {
  hash: string;
  time: number;
  block: number;
}

export interface PoCActivity extends BaseTxn {
  challenger: string;
  challenger_location: string;
  beaconer: string;
  beaconer_location: string;
  valid_count: number;
  invalid_count: number;
  witnesses: HotspotWitness[];
}

interface HotspotWitnessActivity extends PoCActivity {
  type: "witnesses";
  distance: number;
  valid: boolean;
}

interface HotspotChallengerActivity extends PoCActivity {
  type: "challengers";
}

interface HotspotChallengeeActivity extends PoCActivity {
  type: "challengees";
}

interface HotspotRewardActivity extends BaseTxn {
  type: "rewards";
  amount: number;
}

interface HotspotDataPacketActivity extends BaseTxn {
  type: "data_packets";
  num_dcs: number;
  location: string; // h3r12
  num_packets: number;
}

type Activity =
  | HotspotWitnessActivity
  | HotspotChallengerActivity
  | HotspotChallengeeActivity
  | HotspotRewardActivity
  | HotspotDataPacketActivity;

interface HotspotActivitiesData {
  witnesses: HotspotWitnessActivity[];
  challengers: HotspotChallengerActivity[];
  challengees: HotspotChallengeeActivity[];
  rewards: HotspotRewardActivity[];
  data_packets: HotspotDataPacketActivity[];
}

interface HotspotActivitiesResponse {
  object: "list";
  url: "/hotspot/activities/118P6WUe7RAt33v5ME5EUpM9C6YiL9SzFaUFbb5H1iNTNGZTN7w/";
  limit: number; // 100 by defaultl
  data: HotspotActivitiesData;
}

const CONFIG: {
  [type: string]: {
    icon: any;
    background: string;
    name: string;
    hotspotContextName: String;
  };
} = {
  data_packets: {
    icon: DocumentIcon,
    background: "bg-yellow-500",
    name: "Data transfer",
    hotspotContextName: "Transferred data packets"
  },
  rewards: {
    icon: GiftIcon,
    background: "bg-green-500",
    name: "Reward",
    hotspotContextName: "Received mining rewards"
  },
  challengers: {
    icon: SpeakerphoneIcon,
    background: "bg-indigo-500",
    name: "Challenger",
    hotspotContextName: "Challenged beaconer"
  },
  challengees: {
    icon: StatusOnlineIcon,
    background: "bg-hotspotty-400",
    name: "Beacon",
    hotspotContextName: "Sent beacon"
  },
  witnesses: {
    icon: EyeIcon,
    background: "bg-green-500",
    name: "Witness",
    hotspotContextName: "Witnessed beacon"
  }
};

const renderIcon = (activity: Activity) => {
  let Icon = CONFIG[activity.type].icon;
  let background = CONFIG[activity.type].background;

  if (activity.type === "witnesses") {
    Icon = activity.valid ? EyeIcon : EyeOffIcon;
    background = activity.valid ? "bg-blue-500" : "bg-red-500";
  }

  return (
    <span
      className={classNames(
        background,
        "h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white dark:ring-gray-900"
      )}
    >
      <Icon className="h-5 w-5 text-white" aria-hidden="true" />
    </span>
  );
};

const renderTitle = (activity: Activity) => {
  switch (activity.type) {
    case "witnesses":
      return activity.valid ? "Witnessed beacon" : "Witnessed beacon (invalid)";
    default:
      return CONFIG[activity.type].hotspotContextName;
  }
};
